import React from "react";

import { successToast, useToast } from "../../../components";
import MediaPlayer, {
  MediaPlayerProps,
} from "../../components/MediaPlayer/MediaPlayer";
import {
  MEDIA_VIDEO_HEIGHT_RATIO,
  MediaPlayerV2,
  MediaPlayerV2Props,
} from "../../components/MediaPlayerV2/MediaPlayerV2";
import {
  CallNoteType,
  CallSpeakerFragment,
  Clip,
  useClipShareQuery,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import useClipNotes from "./useClipNotes";
import useClipTranscript from "./useClipTranscript";

type ClipVideoProps = Omit<
  MediaPlayerProps,
  | "speakers"
  | "duration"
  | "onAddNote"
  | "notes"
  | "segments"
  | "shareableUsers"
  | "shareableUsersLoading"
> &
  Pick<MediaPlayerV2Props, "showVideo"> & {
    clip: Pick<Clip, "duration" | "id"> & { speakers: CallSpeakerFragment[] };
    callId: string;
  };

const ClipVideo: React.FC<ClipVideoProps> = ({
  clip,
  callId,
  showVideo,
  ...videoProps
}) => {
  const toast = useToast();
  const useMediaPlayerV2 = useFeatureFlag("media-player-v2");

  const { onAddNote, notes } = useClipNotes({
    callId,
    clipId: clip.id,
    onAddCallNoteCompleted(data) {
      const note = data.addNewCallNote?.callNote;
      if (note) {
        successToast(
          toast,
          `${note.type === CallNoteType.Note ? "Note" : "Reaction"} added`,
          { position: "top" }
        );
      }
    },
  });

  const { transcript } = useClipTranscript({ clipId: clip.id, callId });

  const shareQuery = useClipShareQuery({ variables: { clipId: clip.id } });

  const sharedProps = {
    ...videoProps,
    speakers: clip.speakers,
    duration: clip.duration,
    onAddNote,
    notes: notes ?? [],
    segments: transcript,
    shareableUsers: shareQuery.data?.clip?.shareableUsers,
    shareableUsersLoading: shareQuery.loading,
  };

  if (useMediaPlayerV2) {
    return (
      <MediaPlayerV2
        {...sharedProps}
        showVideo={showVideo}
        heightRatio={MEDIA_VIDEO_HEIGHT_RATIO}
      />
    );
  }

  return <MediaPlayer {...sharedProps} />;
};

export default ClipVideo;
